import * as Yup from 'yup';
import * as idb from 'idb-keyval';
import {
  AppointmentDepositPlacement,
  AppointmentType,
  Roles,
} from '@prisma/client';
import { Appointment, GeneralRequest, Option } from '../../interfaces';
import { testPhoneNumbervalue } from '../global/helper';
import { isDepositPlacementRequired } from '../../helpers/appointments';

export const DEFAULT_APT_LENGTH = '120';
export const DEFAULT_CONSULT_LENGTH = '15';

export interface AdditionalAppointment {
  appointmentLengthInMinutes: string;
  startDateTime: string;
  endDateTime: string;
}

export interface CreateAptValues {
  name: string;
  firstName: string;
  lastName?: string;
  email?: string;
  phoneNumber: string;
  employeeId: string;
  customerId?: string;
  serviceId?: string;
  appointmentLengthInMinutes?: string;
  depositPrice?: number;
  priceType: string;
  minPrice?: number;
  maxPrice?: number;
  hourlyPrice?: number;
  price?: number;
  tattooLocation?: string;
  tattooSize?: string;
  tattooColor?: string;
  tattooDetails?: string;
  startDateTime: string;
  endDateTime: string;
  appointmentType: string;
  isBookingLink: boolean;
  piercingLocationId?: string;
  locationCategory?: string;
  dob?: string;
  internalNotes?: string;
  refImgsId?: string[];
  newImages?: ImageItem[];
  imagesToDelete?: string[];
  businessId?: string;
  requestWaiver?: boolean;
  businessTripId?: string;
  allowOverlapping?: boolean;
  additionalAppointments?: AdditionalAppointment[];
  depositPlacement?: AppointmentDepositPlacement;
  depositPaid?: boolean;
  role?: Roles;
  isFromExistingAppointment?: boolean;
}

export const initFormValues: CreateAptValues = {
  name: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  customerId: '',
  employeeId: '',
  appointmentLengthInMinutes: DEFAULT_APT_LENGTH,
  depositPrice: 0,
  priceType: 'FIXED',
  minPrice: 0,
  maxPrice: 0,
  price: 0,
  hourlyPrice: 0,
  tattooLocation: '',
  tattooSize: '',
  tattooColor: '',
  tattooDetails: '',
  startDateTime: '',
  endDateTime: '',
  appointmentType: '',
  dob: '',
  serviceId: '0',
  piercingLocationId: '',
  locationCategory: '',
  internalNotes: '',
  refImgsId: [],
  requestWaiver: false,
  allowOverlapping: false,
  businessTripId: '',
  depositPlacement: '' as AppointmentDepositPlacement,
  isBookingLink: false,
  isFromExistingAppointment: false,
};

export interface ImageItem {
  id: string;
  url: string;
  isUploaded?: boolean;
}

export const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().nullable().optional(),
  email: Yup.string().email('Invalid email').nullable().optional(),
  phoneNumber: Yup.string()
    .test({
      test: testPhoneNumbervalue,
      message: 'Invalid phone number',
    })
    .required('Phone number is required'),
  dob: Yup.string().nullable().optional(),
  employeeId: Yup.string().required('Tattoo artist is required'),
  appointmentLengthInMinutes: Yup.number(),
  depositPrice: Yup.number(),
  priceType: Yup.string().required('Please select a price type'),
  price: Yup.number().when('priceType', {
    is: 'FIXED',
    then: schema => schema.required('Price is required'),
    otherwise: schema => schema.nullable(),
  }),
  minPrice: Yup.number().when('priceType', {
    is: 'RANGE',
    then: schema => schema.required('Minimum price is required'),
    otherwise: schema => schema.nullable(),
  }),
  maxPrice: Yup.number().when('priceType', {
    is: 'RANGE',
    then: schema => schema.required('Maximum price is required'),
    otherwise: schema => schema.nullable(),
  }),
  hourlyPrice: Yup.number().when('priceType', {
    is: 'HOURLY',
    then: schema => schema.required('Hourly rate is required'),
    otherwise: schema => schema.nullable(),
  }),
  tattooLocation: Yup.string(),
  tattooSize: Yup.string(),
  tattooColor: Yup.string(),
  tattooDetails: Yup.string(),
  requestWaiver: Yup.boolean().nullable().optional(),
  allowOverlapping: Yup.boolean().nullable().optional(),
  businessTripId: Yup.string().nullable().optional(),
  appointmentType: Yup.mixed<AppointmentType>()
    .oneOf(Object.values(AppointmentType))
    .required(),
  startDateTime: Yup.string()
    .required()
    .when(['appointmentType', 'isBookingLink'], {
      is: (appointmentType: AppointmentType, isBookingLink: boolean) =>
        appointmentType === AppointmentType.WALK_IN || isBookingLink,
      then: schema => schema.notRequired(),
    }),
  additionalAppointments: Yup.array()
    .of(
      Yup.object().shape({
        appointmentLengthInMinutes: Yup.number().required(),
        startDateTime: Yup.string().required(),
        endDateTime: Yup.string().required(),
      }),
    )
    .optional(),
  depositPlacement: Yup.mixed<AppointmentDepositPlacement>()
    .oneOf(Object.values(AppointmentDepositPlacement))
    .transform(val => val || undefined)
    .test(
      'deposit-placement-required',
      'Deposit placement is required',
      // eslint-disable-next-line func-names, prefer-arrow-callback
      function (depositPlacement, context) {
        const { additionalAppointments, depositPrice } = context.parent;
        const isRequired = isDepositPlacementRequired(
          additionalAppointments,
          depositPrice,
        );
        if (!isRequired) return true;
        const isValid =
          !!depositPlacement &&
          Object.values(AppointmentDepositPlacement).includes(depositPlacement);
        return isValid;
      },
    ),
});

export type AppointmentFormValues = Yup.InferType<typeof validationSchema>;

export const depositOptions: Option[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export const colorOptions: Option[] = [
  { value: 'Black & Grey' },
  { value: 'Color' },
  { value: 'Linework only' },
];

export const sizeOptions: Option[] = [
  { value: 'Extra Small', label: 'Small (less than 2x2 inches)' },
  { value: 'Small', label: 'Medium (less than 4x4 inches)' },
  { value: 'Medium', label: 'Large (less than 6x6 inches)' },
  { value: 'Large', label: 'Extra Large (half sleeve & bigger)' },
];

export const priceOptions: Option[] = [
  { value: 'FIXED', label: 'Set Price' },
  { value: 'RANGE', label: 'Price Range' },
  { value: 'HOURLY', label: 'Hourly Price' },
];

export const locationOptions: Option[] = [
  { value: 'Ankle' },
  { value: 'Back - Full' },
  { value: 'Back - Lower' },
  { value: 'Back - Upper' },
  { value: 'Behind Ear' },
  { value: 'Butt' },
  { value: 'Calf' },
  { value: 'Chest' },
  { value: 'Elbow' },
  { value: 'Foot' },
  { value: 'Forearm - Inner' },
  { value: 'Forearm - Outer' },
  { value: 'Full Sleeve' },
  { value: 'Half Sleeve' },
  { value: 'Hand' },
  { value: 'Hip' },
  { value: 'Knee' },
  { value: 'Leg Sleeve' },
  { value: 'Neck' },
  { value: 'Pelvis' },
  { value: 'Ribs' },
  { value: 'Shin' },
  { value: 'Other' },
  { value: 'Shoulder Blade' },
  { value: 'Sternum' },
  { value: 'Stomach' },
  { value: 'Thigh - Back' },
  { value: 'Thigh - Front' },
  { value: 'Throat' },
  { value: 'Upper Arm - inner' },
  { value: 'Upper Arm - outer' },
  { value: 'Wrist' },
];

export const waiverOptions: Option[] = [
  {
    label: 'Collect through Porter',
    value: true,
  },
  {
    label: "I'll collect a waiver myself",
    value: false,
  },
];

export const allowOverlappingOptions: Option[] = [
  {
    label: 'Allow appointments to be scheduled at the same time',
    value: true,
  },
  {
    label: 'Block calendar during this consultation',
    value: false,
  },
];

export const getExistingImgList = (
  req: Appointment | GeneralRequest,
): ImageItem[] => {
  const imageList = req?.refImgsId;
  if (!imageList?.length) return [];
  return imageList.map((url: string) => ({
    id: url,
    url,
    isUploaded: true,
  }));
};

const SESSION_STORAGE_KEY = 'new-appt-images';
export const handlePersistedFiles = async () => {
  if (!window.sessionStorage) return [];
  const storedFiles = JSON.parse(
    sessionStorage.getItem(SESSION_STORAGE_KEY) || '[]',
  );
  const newImgs: ImageItem[] = [];
  for (let i = 0; i < storedFiles.length; i++) {
    const fileId = storedFiles[i];
    const file = (await idb.get(fileId)) as File | undefined;
    if (!file?.size) continue;
    newImgs.push({ id: fileId, url: URL.createObjectURL(file) });
  }
  return newImgs;
};

export const persistFiles = async (
  newImages: ImageItem[],
  newFiles: File[],
) => {
  if (!window.sessionStorage) return;
  const storedFiles = JSON.parse(
    sessionStorage.getItem(SESSION_STORAGE_KEY) || '[]',
  );
  const newStoredFiles = Array.from(
    new Set([...storedFiles, ...newImages.map(img => img.id)]),
  );
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newStoredFiles));
  newImages.forEach((img, i) => idb.set(img.id, newFiles[i]));
};

export const removePersistedFiles = async (fileIds: string[]) => {
  if (!window.sessionStorage) return;
  const storedFiles = JSON.parse(
    sessionStorage.getItem(SESSION_STORAGE_KEY) || '[]',
  );
  const newStoredFiles = storedFiles.filter(
    (fileId: string) => !fileIds.includes(fileId),
  );
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newStoredFiles));
  fileIds.forEach(id => idb.del(id));
};
